import { Field, Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import {
  Button,
  ButtonGroup,
  Fieldset,
  Input,
  ModalBox,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'

import { useOrderGroupedByShipmentsQuery } from '../../queries/useOrderGroupedByShipmentsQuery'
import { useUpdatePhoneMutation } from '../../queries/useUpdatePhoneMutation'
import * as S from './SupporterPhoneModal.styles'

type Values = {
  phoneNumber: string
}

export type SupporterPhoneModalProps = {
  orderId: string
  initialValues: Values
}

const ValidationSchema: Yup.SchemaOf<Values> = Yup.object().shape({
  phoneNumber: Yup.string().required(),
})

export const SupporterPhoneModal = ({ orderId, initialValues }: SupporterPhoneModalProps) => {
  const media = useMediaQueryContext()
  const { invalidateOrderGroupedByShipmentsQuery } = useOrderGroupedByShipmentsQuery([{ orderId }])
  const { close } = useModalContext()

  const { updatePhoneMutation } = useUpdatePhoneMutation({
    onSuccess: () => {
      invalidateOrderGroupedByShipmentsQuery()
    },
    onSettled: () => {
      close()
    },
  })

  const handleSubmit = async (values: Values) => {
    await updatePhoneMutation.mutateAsync([{ orderId, phoneNumber: values.phoneNumber }])
  }

  return (
    <ModalBox title="Edit phone number" onClose={close}>
      <Formik<Values>
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Fieldset.Row>
              <Fieldset.Item>
                <Field name="phoneNumber" component={Input} label="Phone number" fluid />
              </Fieldset.Item>
            </Fieldset.Row>
            <S.ButtonContainer>
              <ButtonGroup
                space="medium"
                justify={media.tablet ? 'right' : undefined}
                direction={media.tablet ? 'row' : 'column-reverse'}
                fullWidth={!media.tablet}
              >
                <Button
                  label="Cancel"
                  disabled={isSubmitting}
                  fullWidth={!media.tablet}
                  onClick={close}
                />
                <Button
                  type="submit"
                  label="Update phone number"
                  appearance="primary"
                  fullWidth={!media.tablet}
                  loading={isSubmitting}
                />
              </ButtonGroup>
            </S.ButtonContainer>
          </Form>
        )}
      </Formik>
    </ModalBox>
  )
}
